import { useEffect, useRef, useState } from "react";
import { Pencil, Eraser, X } from "lucide-react";

const COLORS = ["#FF3B30", "#00E5FF", "#FFD60A", "#FFFFFF"];

// Ephemeral on-screen telestration overlay. Drawings are never saved and are
// cleared whenever the clip changes (parent remounts via key={clip.id}).
export function Telestrator() {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const [enabled, setEnabled] = useState(false);
  const [color, setColor] = useState(COLORS[0]);

  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    // preserve nothing on resize (ephemeral); just match pixels to CSS size
    c.width = rect.width;
    c.height = rect.height;
    const ctx = c.getContext("2d");
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctxRef.current = ctx;
  };

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    if (!enabled) return;
    e.preventDefault();
    drawingRef.current = true;
    const ctx = ctxRef.current;
    const { x, y } = pos(e);
    ctx.strokeStyle = color;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!enabled || !drawingRef.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    ctxRef.current?.clearRect(0, 0, c.width, c.height);
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="telestrator-canvas"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className={`absolute inset-0 h-full w-full z-20 touch-none ${enabled ? "cursor-crosshair" : "pointer-events-none"}`}
      />
      {/* toolbar */}
      <div className="absolute top-3 left-3 z-30 flex items-center gap-2 bg-black/80 backdrop-blur-sm rounded-md p-1.5 border border-border">
        <button data-testid="draw-toggle" onClick={() => setEnabled((v) => !v)}
          className={`h-9 px-3 rounded flex items-center gap-1.5 font-heading font-bold uppercase text-sm tracking-wide transition-colors ${
            enabled ? "bg-primary text-white" : "bg-card text-muted-foreground hover:text-white"}`}>
          <Pencil className="h-4 w-4" strokeWidth={2.5} /> Draw
        </button>
        {enabled && (
          <>
            <div className="flex items-center gap-1">
              {COLORS.map((c) => (
                <button key={c} data-testid={`draw-color-${c}`} onClick={() => setColor(c)}
                  className={`h-6 w-6 rounded-full border-2 transition-transform ${color === c ? "border-white scale-110" : "border-transparent"}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
            <button data-testid="draw-clear" onClick={clear}
              className="h-9 w-9 rounded flex items-center justify-center bg-card text-muted-foreground hover:text-white transition-colors">
              <Eraser className="h-4 w-4" strokeWidth={2.5} />
            </button>
            <button data-testid="draw-close" onClick={() => { clear(); setEnabled(false); }}
              className="h-9 w-9 rounded flex items-center justify-center bg-card text-muted-foreground hover:text-primary transition-colors">
              <X className="h-4 w-4" strokeWidth={2.75} />
            </button>
          </>
        )}
      </div>
    </>
  );
}
